import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PhxActionRowItemDirective } from './phx-action-row-item.directive';
import { Ellipses } from '../icon/icon-map';

interface PhxActionRowOverflowItem {
  buttonText: string;
  disabled: boolean;
  clickHandler: () => void;
}

@Component({
  selector: 'phx-action-row',
  templateUrl: './phx-action-row.component.html',
  styleUrls: ['./phx-action-row.component.scss'],
  host: {
    class: 'phx-action-row',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PhxActionRowComponent implements AfterViewInit, OnDestroy {
  @ContentChildren(PhxActionRowItemDirective) private readonly _rowItems: QueryList<PhxActionRowItemDirective>;
  @ViewChild('container', { read: ElementRef }) private readonly _containerRef: ElementRef<any>; // Need to set to HTMLDivElement later when we have updated Typescript version. Getting error while building Storybook.

  private _visibleItems: HTMLElement[] = [];
  public overflowItems: PhxActionRowOverflowItem[] = [];
  public showOverflowButton: boolean = false;
  public readonly ellipsesIcon: string = Ellipses;

  private _destroy$ = new Subject<void>();
  private _resizeObserver: ResizeObserver;

  constructor(
    private _elementRef: ElementRef,
    private _cdr: ChangeDetectorRef,
    private readonly _ngZone: NgZone
  ) {}

  public ngAfterViewInit(): void {
    // Initialize visible items from menu items
    if (this._rowItems) {
      this._visibleItems = this._rowItems.map(item => item.elementRef.nativeElement);
      this._cdr.detectChanges();
    }
    
    this.setupResizeObserver();
    
    // Wait for content to be rendered
    setTimeout(() => {
      this.calculateOverflow();
    }, 0);

    // Recalculate when menu items change dynamically
    if (this._rowItems) {
      this._rowItems.changes
        .pipe(takeUntil(this._destroy$))
        .subscribe(() => {
          this._visibleItems = this._rowItems.map(item => item.elementRef.nativeElement);
          this._cdr.detectChanges();
          setTimeout(() => {
            this.calculateOverflow();
          }, 0);
        });
    }
  }

  public ngOnDestroy(): void {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    this._destroy$.complete();
  }

  private setupResizeObserver(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => {
        this._ngZone.run(() => {
          this.calculateOverflow();
        });
      });

      this._resizeObserver.observe(this._elementRef.nativeElement);
    }
  }

  private calculateOverflow(): void {
    if (!this._containerRef || !this._visibleItems.length) {
      return;
    }

    const containerWidth = this._containerRef.nativeElement.getBoundingClientRect().width;
    const containerPaddingLeft = parseInt(this._containerRef.nativeElement.computedStyleMap().get('padding-left').toString());
    const containerPaddingRight = parseInt(this._containerRef.nativeElement.computedStyleMap().get('padding-right').toString());
    const containerPadding = containerPaddingLeft + containerPaddingRight;
    const gap = parseInt(this._containerRef.nativeElement.computedStyleMap().get('gap').toString());
    const overflowButtonWidth = 36;

    let totalWidth = containerPadding; // Start with container's horizontal padding
    const overflowing: PhxActionRowOverflowItem[] = [];

    // Reset overflow state
    this._visibleItems.forEach(element => {
      element.style.display = '';
    });

    this.overflowItems = [];
    this.showOverflowButton = false;

    // Calculate which buttons fit
    for (let i = 0; i < this._visibleItems.length; i++) {
      const element = this._visibleItems[i];
      const buttonWidth = element.getBoundingClientRect().width;
      const widthWithGap = totalWidth + buttonWidth + (i > 0 ? gap : 0);

      // Check if we need to reserve space for overflow button
      const needsOverflow = overflowing.length > 0 || i < this._visibleItems.length - 1;
      const requiredWidth = widthWithGap + (needsOverflow ? overflowButtonWidth + gap : 0);

      if (requiredWidth <= containerWidth && overflowing.length === 0) {
        totalWidth = widthWithGap;
      } else {
        element.style.display = 'none';

        const directive = this._rowItems.get(i);
        const overflowMenuItemInfo = this.extractButtonInfo(element, directive);
        
        overflowing.push(overflowMenuItemInfo);
      }
    }

    this.overflowItems = overflowing;
    this.showOverflowButton = overflowing.length > 0;

    this._cdr.markForCheck();
  }

  private extractButtonInfo(element: HTMLElement, directive?: PhxActionRowItemDirective): PhxActionRowOverflowItem {
    const buttonElement = element.querySelector('button') || element as HTMLElement;
    
    if (!buttonElement) {
      return { buttonText: '', disabled: false, clickHandler: () => {} };
    }

    const actionItemLabel = directive?.phxActionItemLabel;
    const buttonText = actionItemLabel || buttonElement.textContent?.trim() || '';
    const disabled = Boolean(buttonElement.getAttribute('disabled'));

    const clickHandler = () => buttonElement.click();

    return { buttonText, disabled, clickHandler };
  }
}
